import { useQuery } from "@tanstack/react-query";
import { useApiService } from "@/hooks/useApiService";
import { getBranchPerformance } from "@/integrations/supabase/report-services";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Terminal } from "lucide-react";
import { BranchPerformanceData } from "@/types/models";
import { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PerformanceBranch = () => {
  const { callService } = useApiService();
  const [isMobile, setIsMobile] = useState(false);

  // Detectar si es móvil
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const { data: performanceData, isLoading, isError, error } = useQuery<BranchPerformanceData[]>({
    queryKey: ['branchPerformance'],
    queryFn: () => callService(getBranchPerformance),
  });
  
  // Sort data by current month's profit in descending order
  const sortedData = performanceData?.sort((a, b) => b.currentMonthProfit - a.currentMonthProfit);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-5 w-1/4" />
              </div>
              <Skeleton className="h-2 w-full rounded-full" />
            </div>
          ))}
        </div>
      );
    }

    if (isError) {
      return (
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            No se pudo cargar el desempeño de las sucursales. Error: {error.message}
          </AlertDescription>
        </Alert>
      );
    }

    if (!sortedData || sortedData.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          No hay datos de desempeño para el período actual.
        </p>
      );
    }
    
    return (
      <TooltipProvider>
        <div className="space-y-4">
          {sortedData.map((branch) => (
            <div key={branch.branchId} className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{branch.branchName}</p>
                <p className="text-sm text-muted-foreground">
                  {branch.currentMonthRentals} alquileres 
                  <span className="text-xs ml-1">
                    (mes anterior: {branch.previousMonthRentals})
                  </span>
                </p>
              </div>
              {isMobile ? (
                // Versión para móviles con Popover
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="h-5 w-full rounded-full bg-secondary relative flex items-center cursor-pointer">
                      {/* Colored bar for current month's profit */}
                      <div
                        className="h-full rounded-full bg-bike-500 flex items-center px-2"
                        style={{ width: `${(branch.currentMonthProfit * 100) / Math.max(branch.previousMonthProfit, 1)}%` }}
                      >
                        {/* Current month profit text - visible only if bar is wide enough */}
                        <span className="text-xs font-medium text-white truncate">
                          ${branch.currentMonthProfit.toFixed(0)}
                        </span>
                      </div>
                      {/* Previous month profit text - always on the right */}
                      <span className="absolute right-2 text-xs font-medium text-white">
                        ${branch.previousMonthProfit.toFixed(0)}
                      </span>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4">
                    <div className="space-y-2">
                      <p className="font-medium">Ganancia:</p>
                      <p className="font-medium">Mes Actual: <span className="font-bold">${branch.currentMonthProfit.toFixed(2)}</span></p>
                      <p className="font-medium">Mes Anterior: <span className="font-bold">${branch.previousMonthProfit.toFixed(2)}</span></p>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                // Versión para escritorio con Tooltip
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="h-5 w-full rounded-full bg-secondary relative flex items-center">
                      {/* Colored bar for current month's profit */}
                      <div
                        className="h-full rounded-full bg-bike-500 flex items-center px-2"
                        style={{ width: `${(branch.currentMonthProfit * 100) / Math.max(branch.previousMonthProfit, 1)}%` }}
                      >
                        {/* Current month profit text - visible only if bar is wide enough */}
                        <span className="text-xs font-medium text-white truncate">
                          ${branch.currentMonthProfit.toFixed(0)}
                        </span>
                      </div>
                      {/* Previous month profit text - always on the right */}
                      <span className="absolute right-2 text-xs font-medium text-white">
                        ${branch.previousMonthProfit.toFixed(0)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Ganancia:</p>
                    <p>Mes Actual: ${branch.currentMonthProfit.toFixed(2)}</p>
                    <p>Mes Anterior: ${branch.previousMonthProfit.toFixed(2)}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      </TooltipProvider>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Desempeño Sucursales</CardTitle>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default PerformanceBranch;